import { router } from '@inertiajs/react';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import * as React from 'react';
import Copyright from '../../internals/components/Copyright';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const handleEdit = (edit_url) => {
  router.get(edit_url);
};

export default function DriversTable({ drivers = [] }) {
  const [deleteDialog, setDeleteDialog] = React.useState({
    open: false,
    driverName: '',
    destroyUrl: '',
    loading: false
  });

  const rows = drivers.map((driver) => ({
    id: driver.id,
    name: driver.name,
    email: driver.email,
    phone: driver.phone || '-',
    carrier_name: driver.carrier_name || '-',
    last_sign_in_at: driver.last_sign_in_at,
    created_at: driver.created_at,
    edit_url: driver.edit_url,
    destroy_url: driver.destroy_url
  }));

  const handleDeleteClick = (destroy_url, driverName) => {
    setDeleteDialog({
      open: true,
      driverName,
      destroyUrl: destroy_url,
      loading: false
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteDialog(prev => ({ ...prev, loading: true }));
    router.delete(deleteDialog.destroyUrl, {
      onSuccess: () => {
        setDeleteDialog({
          open: false,
          driverName: '',
          destroyUrl: '',
          loading: false
        });
      },
      onError: (errors) => {
        setDeleteDialog(prev => ({ ...prev, loading: false }));
        // You could show an error message here
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      driverName: '',
      destroyUrl: '',
      loading: false
    });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      flex: 1.5,
    },
    {
      field: 'phone',
      headerName: 'Telefone',
      width: 150,
      flex: 0.5,
    },
    {
      field: 'carrier_name',
      headerName: 'Transportadora',
      width: 200,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          <BusinessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'last_sign_in_at',
      headerName: 'Último Acesso',
      width: 130,
      flex: 0.5,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Criado em',
      width: 110,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <Tooltip title="Editar motorista">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              onClick={() => handleEdit(params.row.edit_url)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir motorista">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              color="warning"
              onClick={() => handleDeleteClick(params.row.destroy_url, params.row.name)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Motoristas
      </Typography>

      <DataGrid
        checkboxSelection
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableColumnResize
        density="compact"
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir o motorista{' '}
            <strong>{deleteDialog.driverName}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteDialog.loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="warning"
            variant="contained"
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
} 